import React, { useEffect, useState } from 'react';
import { Box, Typography, Grid, TextField, Autocomplete, CircularProgress } from '@mui/material';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import moment from 'moment-timezone';
import { getTimeOffReasons, TimeOffReason } from '../../../../../core/API/scheduler';

interface TimeOffFormProps {
	onSubmit: (values: any) => Promise<void>;
	onCancel: () => void;
	formRef?: React.MutableRefObject<any>;
}

const today = moment().startOf('day');
const oneYearFromNow = moment().add(1, 'year').endOf('day');

const validationSchema = Yup.object({
	startDate: Yup.date()
		.required('Start date is required')
		.min(today.toDate(), 'Cannot request time off for past dates')
		.max(oneYearFromNow.toDate(), 'Cannot request time off more than one year in advance'),
	endDate: Yup.date()
		.required('End date is required')
		.min(Yup.ref('startDate'), 'End date must be after or equal to start date')
		.max(oneYearFromNow.toDate(), 'Cannot request time off more than one year in advance'),
	reason: Yup.string().required('Time off reason is required'),
	notes: Yup.string(),
});

const TimeOffForm: React.FC<TimeOffFormProps> = ({ onSubmit, onCancel: _onCancel, formRef }) => {
	const [timeOffReasons, setTimeOffReasons] = useState<TimeOffReason[]>([]);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		const fetchTimeOffReasons = async () => {
			try {
				const reasons = await getTimeOffReasons();
				setTimeOffReasons(reasons.filter(r => r.active && !r.deleted));
			} catch (error) {
				console.error('Failed to fetch time off reasons:', error);
			} finally {
				setLoading(false);
			}
		};

		fetchTimeOffReasons();
	}, []);

	const initialValues = {
		startDate: '',
		endDate: '',
		reason: '',
		notes: '',
		reasonDescription: '',
		reasonNote: '',
	};

	const handleFormSubmit = async (formValues: typeof initialValues) => {
		// Convert dates to date-only strings (YYYY-MM-DD format, without time)
		const startDateTime = formValues.startDate 
			? moment(formValues.startDate).format('YYYY-MM-DD')
			: null;
		const endDateTime = formValues.endDate 
			? moment(formValues.endDate).format('YYYY-MM-DD')
			: null;

		const { reason, reasonDescription, reasonNote, ...rest } = formValues;
		await onSubmit({
			...rest,
			timeOffReasons: reason,
			reasonDescription,
			reasonNote,
			startDateTime,
			endDateTime,
		});
	};

	return (
		<Formik
			innerRef={formRef}
			initialValues={initialValues}
			validationSchema={validationSchema}
			onSubmit={handleFormSubmit}
		>
			{({ values, setFieldValue, errors, touched }) => (
				<Form>
					<Box sx={{ mt: 3 }}>
						<Typography variant="h6" gutterBottom>
							Request Time Off
						</Typography>
						<Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
							Select the date range for your time off request. Must be a future date.
						</Typography>

						<Grid container spacing={3}>
							<Grid item xs={12} sm={6}>
								<TextField
									fullWidth
									type="date"
									label="Start Date"
									value={values.startDate}
									onChange={(e) => setFieldValue('startDate', e.target.value)}
									InputLabelProps={{ shrink: true }}
									inputProps={{ 
										min: moment().format('YYYY-MM-DD'),
										max: moment().add(1, 'year').format('YYYY-MM-DD')
									}}
									error={touched.startDate && !!errors.startDate}
									helperText={touched.startDate && errors.startDate}
								/>
							</Grid>
							<Grid item xs={12} sm={6}>
								<TextField
									fullWidth
									type="date"
									label="End Date"
									value={values.endDate}
									onChange={(e) => setFieldValue('endDate', e.target.value)}
									InputLabelProps={{ shrink: true }}
									inputProps={{ 
										min: values.startDate || moment().format('YYYY-MM-DD'),
										max: moment().add(1, 'year').format('YYYY-MM-DD')
									}}
									error={touched.endDate && !!errors.endDate}
									helperText={touched.endDate && errors.endDate}
								/>
							</Grid>
							<Grid item xs={12}>
								<Autocomplete
									options={timeOffReasons}
									getOptionLabel={(option) => (typeof option === 'string' ? option : option.name)}
									value={timeOffReasons.find((r) => r['@id'] === values.reason) || null}
									onChange={(_, value) => {
										setFieldValue('reason', value ? value['@id'] : '');
										setFieldValue('reasonDescription', value?.description || '');
										setFieldValue('reasonNote', value?.note || '');
									}}
									isOptionEqualToValue={(option, value) => option['@id'] === value?.['@id']}
									loading={loading}
									disabled={loading}
									renderInput={(params) => (
										<TextField
											{...params}
											label="Reason"
											required
											error={touched.reason && Boolean(errors.reason)}
											helperText={touched.reason && (errors.reason as string)}
											margin="normal"
											fullWidth
										/>
									)}
								/>
								{values.reason && (
									<>
										{values.reasonDescription && (
											<TextField
												fullWidth
												multiline
												rows={2}
												value={values.reasonDescription}
												InputProps={{
													readOnly: true,
												}}
												sx={{ mt: 2 }}
												variant="outlined"
												label="Reason Description"
											/>
										)}
										{values.reasonNote && (
											<TextField
												fullWidth
												multiline
												rows={2}
												value={values.reasonNote}
												InputProps={{
													readOnly: true,
												}}
												sx={{ mt: 2 }}
												variant="outlined"
												label="Reason Note"
											/>
										)}
									</>
								)}
							</Grid>
							<Grid item xs={12}>
								<TextField
									fullWidth
									multiline
									rows={3}
									label="Notes (Optional)"
									value={values.notes}
					onChange={(e) => setFieldValue('notes', e.target.value)}
				/>
			</Grid>
		</Grid>
	</Box>

	</Form>
		)}
	</Formik>
);
};

export default TimeOffForm;